import { QueryKey, useQuery, NetworkMode } from '@tanstack/react-query';
import { fetcher, PPSFetchError, RequestContext, RequestMethods } from './fetcher';
import { useCallback } from 'react';

export interface UseTanstackFetchError<TError = PPSFetchError> {
  status: number;
  text: string;
  payload: TError;
}

export interface QueryOptions {
  suspense?: boolean;
  useErrorBoundary?: boolean | ((error: any) => boolean);
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  retry?: number;
  keepPreviousData?: boolean;
  refetchInterval?: number;
  networkMode?: NetworkMode;
}

export interface UseFetchOptions {
  queryKey: QueryKey;
  url: string;
  method: RequestMethods;
  body?: any;
  context?: RequestContext;
  token?: string;
  headers?: HeadersInit;
}

interface RejectedProps<ResultT> {
  onRejected?: (error: any, retryRequest: (retryProps: UseFetchOptions) => Promise<ResultT>) => any;
}

export function useFetch<TData, TError = PPSFetchError>({
  queryKey,
  url,
  method,
  body,
  context,
  token,
  headers,
  onRejected,
  ...queryOptions
}: UseFetchOptions & QueryOptions & RejectedProps<TData>) {
  const fetch = useCallback(async () => {
    return fetcher<TData>({
      url,
      method,
      token,
      body: body ? JSON.stringify(body) : undefined,
      context,
      headers,
    }).catch((error) => {
      if (onRejected) {
        return onRejected(error, (retryProps) => {
          return fetcher<TData>({
            url: retryProps.url,
            method: retryProps.method,
            token: retryProps.token,
            body: retryProps.body ? JSON.stringify(retryProps.body) : undefined,
            context,
            headers: retryProps.headers,
          });
        });
      } else {
        throw error;
      }
    });
  }, [body, context, headers, method, onRejected, token, url]);

  return useQuery<TData, UseTanstackFetchError<TError>>(queryKey, fetch, queryOptions);
}

export function useQueryFetch<TData, TError = PPSFetchError>({
  queryKey,
  fetcher,
  ...queryOptions
}: {
  fetcher: () => Promise<TData>;
  queryKey: QueryKey;
} & QueryOptions) {
  return useQuery<TData, UseTanstackFetchError<TError>>(queryKey, fetcher, queryOptions);
}
